import type { Market } from 'types/models/market'
import type { MetaSEO, OtherMeta, UseMetaOptions } from 'types/seo'

import type { Route } from '~/types/route'
import { OG_DEFAULT_IMAGE, OG_THUMBNAIL } from 'configuration/global.configuration'
import { getPrimaryMeta, getSeoHeadData, getSeoTitle } from 'lib/seo'
import { getAllMarkets } from 'lib/storefronts'
import { useStoryblokStore } from '~/stores/storyblok'

const BASE_URL = 'https://www.happysocks.com'

function getTwitterMeta(SEO: MetaSEO, storeCode: string, options?: { route: Pick<Route, 'fullPath'> }): OtherMeta[] {
  const storyblokStore = useStoryblokStore()
  const defaultOgImage = storyblokStore.getConstant(OG_THUMBNAIL)?.value || OG_DEFAULT_IMAGE
  const tTitle = SEO.twitter_title ? SEO.twitter_title : getSeoTitle(SEO, storeCode, options)
  const tDescription = SEO.twitter_description || SEO.description || ''
  const tImage = SEO.twitter_image ? SEO.twitter_image : SEO.img ? SEO.img : defaultOgImage

  return [
    { property: 'twitter:title', content: tTitle },
    { property: 'twitter:description', content: tDescription },
    { property: 'twitter:image', content: tImage },
  ]
}

function getOpenGraphMeta(
  SEOTitle: string,
  description: string,
  url: string,
  locale: string,
  SEO: MetaSEO,
): OtherMeta[] {
  const storyblokStore = useStoryblokStore()
  const defaultOgImage = storyblokStore.getConstant(OG_THUMBNAIL)?.value || OG_DEFAULT_IMAGE
  const ogImage = SEO.og_image || SEO.img || defaultOgImage
  const ogDescription = description || SEO.og_description || SEO.description

  const meta: OtherMeta[] = [
    { property: 'og:title', content: SEOTitle },
    { property: 'og:image', content: ogImage },
    { property: 'og:site_name', content: 'Happy Socks' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: url },
    { property: 'og:locale', content: locale },
  ]

  if (ogDescription)
    meta.push({ property: 'og:description', content: ogDescription })

  return meta
}

function getOgLocaleFromMarket(market: Market): OtherMeta {
  return {
    property: 'og:locale:alternate',
    content: market.locale.iso,
  }
}

function getOGLocaleAlternatesMeta(markets: Market[]): OtherMeta[] {
  return markets.map(getOgLocaleFromMarket)
}

function getMeta(
  SEOTitle: string,
  description: string,
  meta_robots: string[],
  url: string,
  locale: Market['locale'],
  markets: Market[],
  SEO: MetaSEO,
  storeCode: string,
) {
  return [
    ...getPrimaryMeta(SEOTitle, description, meta_robots),
    ...getOpenGraphMeta(SEOTitle, description, url, locale.iso, SEO),
    ...getOGLocaleAlternatesMeta(markets),
    ...getTwitterMeta(SEO, storeCode),
  ]
}

export function useMeta({ story, storefront, market, section, SEO: SEOStory }: UseMetaOptions) {
  const config = useRuntimeConfig()
  const storeCode = computed(() => storefront.storefrontCode)
  const locale = computed(() => market.locale)
  const markets = getAllMarkets()
  const storyValue = ref<typeof story | null>(null)
  const SEOStoryValue = ref<typeof SEOStory | null>(null)

  watchEffect(() => {
    if (story)
      storyValue.value = toValue(story)
  })

  watchEffect(() => {
    if (SEOStory)
      SEOStoryValue.value = toValue(SEOStory)
  })

  const SEO = computed(() => storyValue.value?.content.SEO || SEOStoryValue.value)
  const meta_robots = computed(() => storyValue.value?.content.meta_robots || [])

  const baseUrl = computed(() => config.public.baseUrlClient || BASE_URL)
  const url = computed(() => {
    const fullSlug = storyValue.value?.full_slug
    const url = `${baseUrl.value}/${fullSlug}`
    return url.includes(`/${storeCode.value}/`)
      ? url
      : `${baseUrl.value}/${storeCode.value}/${fullSlug}`
  })

  const SEOTitle = computed(() => getSeoTitle(SEO.value, storeCode.value))
  const description = computed(() => section?.description || SEO.value.description || '')

  return {
    getMeta: getMeta.bind(
      null,
      SEOTitle.value,
      description.value,
      meta_robots.value,
      url.value,
      locale.value,
      markets,
      SEO.value,
      storeCode.value,
    ),
    getPrimaryMeta: getPrimaryMeta.bind(null, SEOTitle.value, description.value, meta_robots.value),
    getTwitterMeta: getTwitterMeta.bind(null, SEO.value, storeCode.value),
    getOpenGraphMeta: getOpenGraphMeta.bind(
      null,
      SEOTitle.value,
      description.value,
      url.value,
      locale.value.iso,
      SEO.value,
    ),
    getOGLocaleAlternatesMeta: getOGLocaleAlternatesMeta.bind(null, markets),

    title: SEOTitle,
    meta: computed(() =>
      getMeta(
        SEOTitle.value,
        description.value,
        meta_robots.value,
        url.value,
        locale.value,
        markets,
        SEO.value,
        storeCode.value,
      ),
    ),
    primaryMeta: computed(() =>
      getPrimaryMeta(SEOTitle.value, description.value, meta_robots.value),
    ),
    twitterMeta: computed(() => getTwitterMeta(SEO.value, storeCode.value)),
    openGraphMeta: computed(() =>
      getOpenGraphMeta(SEOTitle.value, description.value, url.value, locale.value.iso, SEO.value),
    ),
    ogLocaleAlternatesMeta: computed(() => getOGLocaleAlternatesMeta(getAllMarkets())),
    seoHeadData: computed(() => getSeoHeadData(SEO.value, storeCode.value)),
  }
}
